import { memo, useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useTracking,
  type DotState,
  type TrackFile,
  type TrackModule,
  type TrackingView as TView,
} from "../lib/tracking";
import { anchored } from "../lib/anchor";
import { api } from "../api/client";
import type { Project } from "../api/types";
import { AssignProofer } from "./AssignProofer";
import { ProjectEssentials } from "./ProjectEssentials";
import { SectionHead } from "./SectionHead";

type ModKey = TrackModule["key"];

/**
 * The expanded row's Tracking View — a module × chapter matrix over a linked
 * project, plus a flexible Project Essentials repository. The matrix (durations,
 * per-cell status dots, flag counts) is REAL, recomputed server-side; the
 * chapter include/exclude selection, per-module Status pill + Note, and the
 * essentials are editable and persisted on the workflow. Ported from the
 * tracking-view prototype; classes are scoped under `.wf-track` (see styles.css).
 */

// ---- status palettes (inline colours, matching the prototype) --------------
type Stat = { label: string; dot: string; bg: string; fg: string };
const MODULE_ST: Record<string, Stat> = {
  announced: { label: "Announced", dot: "#8A90AC", bg: "#EFEFF6", fg: "#555B78" },
  pending_chain: { label: "Pending Chain", dot: "#6B72D4", bg: "#ECEDF6", fg: "#484FB0" },
  assigning: { label: "Assigning", dot: "#0E9AA8", bg: "#DCF2F4", fg: "#0B7C87" },
  in_progress: { label: "In Progress", dot: "#2540F6", bg: "#E6ECFE", fg: "#2540F6" },
  confirm: { label: "Confirm", dot: "#E0890A", bg: "#FBEDD5", fg: "#A9690A" },
  on_hold: { label: "On Hold", dot: "#E23054", bg: "#FCE7EC", fg: "#C42A48" },
  complete: { label: "Complete", dot: "#18A97A", bg: "#DEF3EA", fg: "#12855F" },
  // Deliberately colourless: a step this project doesn't need should read as
  // switched off, not as another live state competing for the eye.
  not_required: { label: "Not Required", dot: "#A9AEC0", bg: "#F4F5F8", fg: "#8A90A6" },
};
const MODULE_ORDER = ["announced", "pending_chain", "assigning", "in_progress", "confirm", "on_hold", "complete", "not_required"];
/** A step the PM has switched off for this project — its dots stop meaning
    anything, so the whole row is dimmed rather than left looking outstanding. */
const isOff = (status: string) => status === "not_required";

// ---- small helpers ---------------------------------------------------------
const pad2 = (n: number) => String(n).padStart(2, "0");
const hms = (s: number) => `${Math.floor(s / 3600)}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(Math.floor(s % 60))}`;
// Rolls over past the hour. Audiobook chapters routinely run over 60 minutes,
// and "68:22" - or worse, "180:00" - reads as a mistake rather than a length.
const mmss = (s: number) =>
  s >= 3600 ? hms(s) : `${Math.floor(s / 60)}:${pad2(Math.floor(s % 60))}`;
// Keep both ends so files that share a long prefix (e.g. BK_ADBL_066369_…s1b)
// stay distinguishable in a narrow header; the full name is in the tooltip.
const midTrunc = (s: string, max = 18): string => {
  if (s.length <= max) return s;
  const head = Math.ceil((max - 1) * 0.55);
  return s.slice(0, head) + "…" + s.slice(s.length - (max - 1 - head));
};

// ---- inline icons (compact, ported from the prototype) ---------------------
const I: Record<string, ReactElement> = {
  tr: <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 4.5h8M4 8h8M4 11.5h5" /></svg>,
  pr: <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5l3 3 7-8" /></svg>,
  re: <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7" cy="7" r="4.2" /><path d="M10.5 10.5l3 3" strokeLinecap="round" /></svg>,
  tk: <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 10l2.5-4 2 3L9 4l2.5 5" /><path d="M2 13h12" /></svg>,
  up: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 10.5v-7M5 6l3-3 3 3M3 12.5h10" /></svg>,
  chev: <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6l4 4 4-4" /></svg>,
  toc: <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="2.5" y="2.5" width="11" height="11" rx="1.5" /><path d="M6.5 2.5v11M2.5 6h11" /></svg>,
  check: <svg viewBox="0 0 16 16" width="11" height="11" fill="none"><path d="M3.5 8.4l3 3 6-6.6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
};
const MOD_ICON: Record<string, ReactElement> = { upload: I.up, transcription: I.tr, proof: I.pr, review: I.re, tech: I.tk, toc: I.toc };

// ---- an uncontrolled editable text cell (no caret jumps on re-render) -------
// `singleLine` fields (a field name, a link/password/ID value) commit on Enter —
// the intuitive "I'm done" action — instead of inserting a newline. The Notes
// column omits it, so a note can still span lines.
const Editable = memo(function Editable({ initial, className, placeholder, onCommit, singleLine }: {
  initial: string; className: string; placeholder: string; onCommit: (v: string) => void; singleLine?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (ref.current) ref.current.textContent = initial; }, []); // set once; browser owns it after
  return (
    <div
      ref={ref}
      className={className}
      contentEditable
      suppressContentEditableWarning
      data-ph={placeholder}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={singleLine ? (e) => {
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();            // no newline…
          e.currentTarget.blur();        // …blur commits via onBlur
        }
      } : undefined}
      onBlur={(e) => onCommit(e.currentTarget.textContent ?? "")}
    />
  );
});

// ---- fixed-position menu (status picker / add-field) -----------------------
type MenuState =
  | { kind: "status"; idx: number; rect: DOMRect }
  | null;

function positioned(rect: DOMRect): CSSProperties {
  // Was clamping the TOP to the window's bottom edge, which put the whole menu
  // below the fold rather than keeping any of it on screen. Flipping above is
  // the only move that actually shows the options.
  return anchored(rect, { width: 192, zIndex: 60 });
}

const dotClass = (s: DotState) => (s === "na" ? "" : `dot ${s}`);

export function TrackingView({ workflowId }: { workflowId: string }) {
  const { view, isLoading, edit } = useTracking(workflowId, true);
  const [menu, setMenu] = useState<MenuState>(null);
  const qc = useQueryClient();

  // Essentials are the PROJECT's, so we render the project's own section here
  // rather than a second copy of it. Same query key as the project hub, so the
  // two share a cache entry and an edit in either place lands in both.
  const linkedId = view?.project_id;
  const { data: project, refetch: refetchProject } = useQuery({
    queryKey: ["project", linkedId],
    queryFn: () => api<Project>(`/api/projects/${linkedId}`),
    enabled: !!linkedId,
  });

  // An essential can be canonical on the project AND shown on the board (the
  // review/password/folder links are the Links cell), so refresh both.
  function onEssentialsChanged() {
    refetchProject();
    qc.invalidateQueries({ queryKey: ["tracking", workflowId] });
    qc.invalidateQueries({ queryKey: ["workflows"] });
  }

  // close menus on outside click / esc / scroll
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenu(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  if (isLoading) return <div className="wf-track wf-track-msg">Loading tracking…</div>;
  if (!view) return null;
  if (!view.linked)
    return <div className="wf-track wf-track-msg">Link a project to this row to track its chapters and modules here.</div>;

  const files = view.files ?? [];
  const modules = view.modules ?? [];
  const n = files.length;
  const selSecs = files.reduce((a, f) => a + (f.selected ? f.duration_seconds : 0), 0);

  // flag summary (real)
  const fsum = files.reduce(
    (a, f) => ({ misread: a.misread + f.flags.misread, missing: a.missing + f.flags.missing, added: a.added + f.flags.added, pause: a.pause + f.flags.pause }),
    { misread: 0, missing: 0, added: 0, pause: 0 }
  );
  const coverage = view.coverage;
  const picked = view.pickups?.picked ?? 0;
  const addressed = view.pickups?.addressed ?? 0;

  // ---- edit ops ----
  const setSel = (idx: number, on: boolean) => edit((v) => ({ ...v, files: v.files!.map((f, i) => (i === idx ? { ...f, selected: on } : f)) }));
  const setAll = (on: boolean) => edit((v) => ({ ...v, files: v.files!.map((f) => ({ ...f, selected: on })) }));
  const setModStatus = (idx: number, k: string) => edit((v) => ({ ...v, modules: v.modules!.map((m, i) => (i === idx ? { ...m, status: k } : m)) }));
  const setModNote = (idx: number, note: string) => edit((v) => ({ ...v, modules: v.modules!.map((m, i) => (i === idx ? { ...m, notes: note } : m)) }));

  const statPill = (idx: number, statusId: string) => {
    const s = MODULE_ST[statusId] ?? MODULE_ST[MODULE_ORDER[0]];
    return (
      <button className="mstat" style={{ background: s.bg, color: s.fg }} title="Set status"
        onClick={(e) => { e.stopPropagation(); setMenu({ kind: "status", idx, rect: (e.currentTarget as HTMLElement).getBoundingClientRect() }); }}>
        <span className="d" style={{ background: s.dot }} />
        <span className="mstat-l">{s.label}</span>
        <span className="cv">{I.chev}</span>
      </button>
    );
  };

  // The manuscript/ToC assets link to the project's canonical endpoints; upload
  // and editing happen in the project's Essentials section, not here.
  const projectId = view.project_id;

  // With no chapters (a project not yet proofed/mapped — e.g. spec-check-only
  // "final masters") there are no file columns; use a single flexible middle
  // column so the essentials + module rows still lay out cleanly (a `span 0` /
  // `repeat(0,…)` grid is invalid CSS and collapses the whole matrix).
  // Columns = the real files PLUS a placeholder wherever the delivery appears to
  // be missing one. Built here (not on the server) because it's purely how the
  // matrix lays out; every module row maps this same list so the dots stay
  // under the right file.
  type Col =
    | { kind: "file"; file: TrackFile; index: number }
    | { kind: "gap"; label: string; after: string };
  const cols: Col[] = [];
  files.forEach((f, i) => {
    for (const g of coverage?.gaps ?? []) {
      if (g.insert_before && g.insert_before === f.audio_filename) {
        cols.push({ kind: "gap", label: g.label, after: g.after_file });
      }
    }
    cols.push({ kind: "file", file: f, index: i });
  });
  const nCols = cols.length;
  // Pad to the producer's own numbering width so a gap ("008") and the files
  // around it ("007"/"009") read as one sequence, not two schemes.
  const padN = (v: number) => String(v).padStart(Math.max(2, coverage?.width ?? 2), "0");

  const gridCols = n > 0
    ? `176px 166px repeat(${nCols}, 84px) 200px`
    : `176px 166px minmax(220px, 1fr) 200px`;

  return (
    <div className="wf-track">
      <div className="tv-inner">
        {/* The view title outranks the section heads below it (17px vs 15px) —
            it names the whole panel. `tv-stick` is what keeps this row on
            screen: the board scrolls horizontally, so a plain block child is as
            wide as the full scroll width and `space-between` used to push the
            runtime readout clean out of view. */}
        <div className="tv-stick tv-head">
          <div className="tv-title">
            <h3>Tracking view</h3>
            <span className="muted">{view.project_name} · {n > 0 ? `${n} audio file${n === 1 ? "" : "s"}` : "not proofed yet"}</span>
          </div>
          {n > 0 && (
            <div className="tv-rt">
              <span className="lbl">Selected runtime</span>
              <span className="big">{hms(selSecs)}</span>
              <span className="frac">{(selSecs / 3600).toFixed(2)} h</span>
            </div>
          )}
        </div>

        {/* All three sections use the hub's SectionHead — same rank, same look.
            Actions belong in its `right` slot, which is what it was built for. */}
        <div className="tv-stick">
          <SectionHead
            title="Chapters × modules"
            sub="Every file against every step of the job."
            right={n > 0 ? (
              <>
                <button className="linkbtn" onClick={() => setAll(true)}>Select all</button>
                <button className="linkbtn mut" onClick={() => setAll(false)}>Clear</button>
              </>
            ) : undefined}
          />
        </div>
        {n === 0 && (
          <p className="hintline">This project has no chapters to track yet — its audio hasn’t been proofed against a manuscript. Project Essentials below still apply.</p>
        )}

        {/* The delivery doesn't look complete. Stated with the evidence, because
            this is a heuristic: name the missing number, what it sits between,
            and whether the manuscript agrees. Both signals agreeing reads as a
            statement; one alone stays hedged. */}
        {(coverage?.gaps?.length || coverage?.uncovered_chapters?.length || coverage?.duplicates?.length) ? (
          <div className={`tv-gapwarn${coverage?.corroborated ? " strong" : ""}`}>
            <span className="tv-gapicon">!</span>
            <div className="tv-gaptext">
              {!!coverage?.gaps?.length && (
                <p>
                  <b>{coverage.corroborated ? "Gap in the audio" : "Likely gap in the audio"}</b>
                  {" — "}
                  {coverage.gaps.length === 1
                    ? <>no file numbered <b>{coverage.gaps[0].label}</b> was delivered</>
                    : <>{coverage.gaps.length} files missing: <b>{coverage.gaps.map((g) => g.label).join(", ")}</b></>}
                  {coverage.gaps[0]?.after_file && coverage.gaps.length === 1 && (
                    <> — the set jumps from <span className="mono">{coverage.gaps[0].after_file}</span> to{" "}
                      <span className="mono">{coverage.gaps[0].before_file}</span></>
                  )}
                  {". "}
                  <span className="muted">{coverage.files_present} of {coverage.expected_total} expected files.</span>
                </p>
              )}
              {!!coverage?.uncovered_chapters?.length && (
                <p className="tv-gapsub">
                  The manuscript has {coverage.uncovered_chapters.length} chapter
                  {coverage.uncovered_chapters.length === 1 ? "" : "s"} no audio covers
                  {": "}
                  <b>{coverage.uncovered_chapters.slice(0, 4).map((c) => c.name).join(" · ")}</b>
                  {coverage.uncovered_chapters.length > 4 && <> …</>}
                </p>
              )}
              {!!coverage?.duplicates?.length && (
                <p className="tv-gapsub">Repeated number{coverage.duplicates.length === 1 ? "" : "s"}: <b>{coverage.duplicates.join(", ")}</b>.</p>
              )}
            </div>
          </div>
        ) : null}

        <div className="mx-scroll">
          <div className="matrix" style={{ gridTemplateColumns: gridCols }}>
            {/* header row */}
            <div className="mx-ca">Module</div>
            <div className="mx-cb">Status</div>
            {nCols > 0
              ? cols.map((c) => {
                  if (c.kind === "gap") {
                    return (
                      <div key={`gap-${c.label}`} className="fhead gap"
                        title={`No file numbered ${c.label} was delivered${c.after ? ` — the set jumps from ${c.after}` : ""}.`}>
                        <span className="fidx">{c.label}</span>
                        <span className="fnm gapnm">missing</span>
                      </div>
                    );
                  }
                  const { file: f, index: i } = c;
                  // The column's number is the file's REAL number off its
                  // filename — NOT its position, which renumbered past a gap and
                  // made an incomplete set look continuous. Falls back to the
                  // position when the set has no credible numbering.
                  const num = f.seq ?? i + 1;
                  // What the project calls the file, not the key it is stored
                  // under: a corrected delivery keeps the chapter's own name.
                  const full = f.shown_filename || f.audio_filename || f.name.replace(/^\d+[_\s-]*/, "");
                  const nm = full.replace(/\.(wav|mp3|flac|aiff?|m4[ab]|ogg)$/i, "");
                  return (
                    <div key={f.chapter_id} className={`fhead${f.selected ? "" : " off"}`} title={`${num}. ${full} · ${mmss(f.duration_seconds)}`} onClick={() => setSel(i, !f.selected)}>
                      <span className="tk">{I.check}</span>
                      <span className="fidx">{padN(num)}</span>
                      <span className="fnm">{midTrunc(nm, 13)}</span>
                      <span className="fdur">{mmss(f.duration_seconds)}</span>
                    </div>
                  );
                })
              : <div className="mx-cmid">— no chapters yet —</div>}
            <div className="mx-cn">Notes</div>

            {/* module rows */}
            {modules.map((mod, mi) => (
              <MatrixRow key={mod.key}>
                <div className={`mx-name${isOff(mod.status) ? " mx-off" : ""}`}>
                  <span className="ml-n">{MOD_ICON[mod.key]}{mod.label}</span>
                  <span className="ml-c">{moduleCount(mod.key, view)}</span>
                </div>
                <div className="mx-status">{statPill(mi, mod.status)}</div>
                {nCols > 0
                  ? cols.map((c) => {
                      // A gap column has no file, so no status to report — render
                      // the hole itself rather than an empty-looking "not started".
                      if (c.kind === "gap") {
                        return <div key={`gap-${mod.key}-${c.label}`} className="mcell gapcell" title={`File ${c.label} was never delivered`} />;
                      }
                      const f = c.file;
                      const st = f.modules[mod.key];
                      const dim = isOff(mod.status) ? " mx-off" : "";
                      // The Review cell is richer than a single dot: a violet
                      // count-dot for pickups and an orange one for still-
                      // unreviewed flags — 0, 1, or 2 dots per chapter.
                      if (mod.key === "review" && st !== "na") {
                        return (
                          <div key={f.chapter_id} className={`mcell${f.selected ? "" : " off"}${dim}`} title={reviewTitle(f)}>
                            <span className="rv-cell">
                              {f.picked > 0 && <span className="rv-badge rv-pick">{f.picked}</span>}
                              {f.unreviewed > 0 && <span className="rv-badge rv-unrev">{f.unreviewed}</span>}
                            </span>
                          </div>
                        );
                      }
                      return (
                        <div key={f.chapter_id} className={`mcell${f.selected ? "" : " off"}${dim}`}
                          title={isOff(mod.status) ? `${mod.label}: not required for this project` : `${mod.label}: ${st}`}>
                          {st === "na" ? <span className="dash">–</span> : <span className={dotClass(st)} />}
                        </div>
                      );
                    })
                  : <div className="mcell mcell-empty" />}
                <Editable className="mx-note" placeholder="Add a note…" initial={mod.notes} onCommit={(v) => setModNote(mi, v)} />
              </MatrixRow>
            ))}

          </div>
        </div>

        {/* Order: the work, then the people doing it, then the reference shelf.
            Assignments sit next to the matrix so "what's proofed" and "who's
            proofing" read together. */}
        {projectId && <AssignProofer projectId={projectId} workflowId={workflowId} files={files} />}

        {/* The project's own Essentials section, not a copy of it — same card,
            same actions (download / open / copy / reveal / edit / add), so the
            board and the project hub can't drift apart. */}
        {projectId && project && (
          <div className="tv-stick tv-ess">
            <ProjectEssentials project={project} onChanged={onEssentialsChanged} />
          </div>
        )}

        <p className="hintline"><b>Status</b> pills and the <b>Notes</b> column are set by the PM — click a pill to change it. Modules use your board statuses, plus <b>Not Required</b> for a step this project doesn't need; <b>Project Essentials</b> are the project's own — editing them here changes them everywhere.</p>

        <div className="mx-foot">
          <div className="legend">
            <span><span className="dot done" />Complete</span>
            <span><span className="dot work" />In progress</span>
            <span><span className="dot wait" />Not started</span>
            <span><span className="dot fail" />Attention</span>
            <span><span className="dash">–</span>n/a</span>
          </div>
          <div className="flagline">
            <span className="fl-h">Flags</span>
            <span className="chip c-mis"><span className="d" />Misread {fsum.misread}</span>
            <span className="chip c-msg"><span className="d" />Missing {fsum.missing}</span>
            <span className="chip c-add"><span className="d" />Added {fsum.added}</span>
            <span className="chip c-pau"><span className="d" />Pause {fsum.pause}</span>
            <span className="fl-rev">· {picked} picked · {addressed} addressed</span>
          </div>
        </div>

        <div className="tv-foot">
          <span className="note">Selected chapters feed the <b>Runtime</b> column on the board — deselect credits / bonus content that shouldn't be billed.</span>
          <button className="gbtn soon">Request pickups <span className="soon-t">soon</span></button>
          <button className="gbtn soon">Export report <span className="soon-t">soon</span></button>
        </div>
      </div>

      {/* menus (fixed, escape the scroll container) */}
      {menu?.kind === "status" && (
        <div className="mmenu" style={positioned(menu.rect)} onClick={(e) => e.stopPropagation()}>
          <div className="mm-t">Set status</div>
          {MODULE_ORDER.map((k) => {
            const cur = modules[menu.idx]?.status;
            return (
              <div key={k} className={`mm-item${cur === k ? " sel" : ""}`}
                onClick={() => { setModStatus(menu.idx, k); setMenu(null); }}>
                <span className="d" style={{ background: MODULE_ST[k].dot }} />{MODULE_ST[k].label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// A matrix "row" is really a run of grid children; this fragment groups them so
// the JSX reads as rows without adding a wrapper element (which would break the
// single-grid layout).
function MatrixRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

// Tooltip for a Review cell — spells out the two count-dots.
function reviewTitle(f: TrackFile): string {
  const parts: string[] = [];
  if (f.picked > 0) parts.push(`${f.picked} pickup${f.picked === 1 ? "" : "s"}`);
  if (f.unreviewed > 0) parts.push(`${f.unreviewed} unreviewed flag${f.unreviewed === 1 ? "" : "s"}`);
  return parts.length ? parts.join(" · ") : "Nothing to review";
}

// Derived per-module count string shown next to the module name.
function moduleCount(key: ModKey, v: TView): string {
  const files = v.files ?? [];
  const applicable = files.filter((f) => f.modules[key] !== "na").length;
  const done = files.filter((f) => f.modules[key] === "done").length;
  if (key === "review") return `${files.reduce((a, f) => a + f.flags.total, 0)} flags`;
  if (key === "tech") return `${done}/${applicable} pass`;
  if (key === "toc") return `${done}/${applicable} clear`;
  return `${done}/${applicable}`;
}
